import { Link } from 'react-router-dom'
import styled, { css, keyframes } from 'styled-components'

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
`

export const HeaderBar = styled.header`
    position: sticky;
    top: 0;
    z-index: 1000;
    background-color: #000000;
    box-shadow: 0 3px 45px rgba(0, 0, 0, 0.15);
    min-height: var(--header-height);
    overflow: visible;
    padding-right: var(--scrollbar-compensation, 0px);

    @media (max-width: 999px) {
        min-height: 0;
    }
`

export const HeaderInner = styled.div`
    --header-pad-y: 30px;
    position: relative;
    z-index: 1200;
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: var(--container);
    margin: 0 auto;
    padding: var(--header-pad-y) var(--page-gutter);
    min-height: var(--header-height);
    gap: 24px;

    @media (max-width: 999px) {
        --header-pad-y: 12px;
        padding: var(--header-pad-y) 20px;
        min-height: 0;
    }
`

export const LogoLink = styled(Link)`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    text-decoration: none;

    img {
        height: 70px;
        width: auto;
        display: block;
    }

    @media (max-width: 999px) {
        img {
            height: 65px;
        }
    }
`

export const DesktopNav = styled.nav`
    display: none;

    @media (min-width: 1000px) {
        display: flex;
        align-items: stretch;
        align-self: stretch;
        margin-left: auto;
    }
`

export const NavList = styled.ul`
    display: flex;
    align-items: stretch;
    list-style: none;
    margin: 0;
    padding: 0;
`

export const NavReveal = styled.span`
    overflow: hidden;
    display: block;
    line-height: 1.3;
    transform: translateZ(0);

    .nav-reveal-text {
        position: relative;
        display: block;
        transition: transform 0.55s cubic-bezier(0.25, 1, 0.33, 1);
    }

    .nav-reveal-text::after {
        content: attr(data-text);
        position: absolute;
        left: 0;
        bottom: -120%;
        transition: transform 0.55s cubic-bezier(0.25, 1, 0.33, 1);
    }
`

export const NavItem = styled.li<{ $mega?: boolean }>`
    position: ${({ $mega }) => ($mega ? 'static' : 'relative')};
    display: flex;
    align-items: center;
    list-style: none;

    > a,
    > button {
        display: inline-flex;
        align-items: center;
        background: transparent;
        border: 0;
        cursor: pointer;
        color: #ffffff;
        font-family: var(--font-display);
        font-size: 28px;
        line-height: 22px;
        font-weight: 400;
        letter-spacing: 1px;
        text-decoration: none;
        padding: 12px 25px;
        transition: color 0.35s var(--ease-out);
    }

    > a:hover,
    > button:hover,
    &[data-open='true'] > a,
    &[data-open='true'] > button {
        color: var(--brand);
    }

    > a:hover ${NavReveal} .nav-reveal-text,
    > button:hover ${NavReveal} .nav-reveal-text,
    &[data-open='true'] > a ${NavReveal} .nav-reveal-text,
    &[data-open='true'] > button ${NavReveal} .nav-reveal-text {
        transform: translateY(-100%);
    }

    > a:hover ${NavReveal} .nav-reveal-text::after,
    > button:hover ${NavReveal} .nav-reveal-text::after,
    &[data-open='true'] > a ${NavReveal} .nav-reveal-text::after,
    &[data-open='true'] > button ${NavReveal} .nav-reveal-text::after {
        transform: translateY(-20%);
    }
`

const dropdownBase = css`
    position: absolute;
    /* Match MegaMenu: flush to the bottom of HeaderBar (NavItem ends above bottom padding). */
    top: calc(100% + var(--header-pad-y));
    left: 0;
    min-width: 240px;
    margin: 0;
    padding: 12px 0;
    list-style: none;
    background: #ffffff;
    border-top: 1px solid #eeeeee;
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.12);
    animation: ${fadeIn} 180ms var(--ease-out);
    z-index: 20;

    &::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 100%;
        height: var(--header-pad-y);
    }
`

export const Dropdown = styled.ul`
    ${dropdownBase}

    a {
        display: block;
        padding: 10px 24px;
        color: #0a0a0a;
        text-decoration: none;
        font-family: var(--font-heading);
        font-size: 15px;
        line-height: 22px;
        font-weight: 500;
        transition: color 200ms var(--ease-out);
    }

    a:hover {
        color: var(--brand);
    }
`

export const MegaMenu = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    width: 100%;
    background: #ffffff;
    border-top: 1px solid #eeeeee;
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.12);
    padding: 40px 8%;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 40px;
    animation: ${fadeIn} 180ms var(--ease-out);
    z-index: 20;

    /* Bridge the gap between the nav trigger and the panel so hover does not drop. */
    &::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 100%;
        height: 48px;
    }
`

export const MegaColumn = styled.div`
    min-width: 0;
`

export const MegaColumnTitle = styled.div`
    font-family: var(--font-display);
    font-size: 30px;
    line-height: 38px;
    letter-spacing: 1px;
    font-weight: 400;
    color: #0a0a0a;
    margin-bottom: 14px;
`

export const MegaLinkList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;

    li + li {
        margin-top: 8px;
    }

    a {
        display: block;
        color: #565656;
        text-decoration: none;
        font-family: var(--font-body);
        font-size: 16px;
        line-height: 24px;
        transition: color 200ms var(--ease-out);
    }

    a:hover {
        color: var(--brand);
    }
`

export const MegaFeatureLink = styled.a`
    display: block;
    text-decoration: none;
    color: inherit;

    .title {
        font-family: var(--font-display);
        font-size: 30px;
        line-height: 38px;
        letter-spacing: 1px;
        font-weight: 400;
        color: #0a0a0a;
        transition: color 200ms var(--ease-out);
    }

    .desc {
        display: block;
        margin-top: 8px;
        font-family: var(--font-body);
        font-size: 15px;
        line-height: 22px;
        color: #cccccc;
        transition: color 200ms var(--ease-out);
    }

    &:hover .title {
        color: var(--brand);
    }

    &:hover .desc {
        color: #0a0a0a;
    }
`

export const MenuToggle = styled.button<{ $open?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border: 0;
    background: transparent;
    cursor: pointer;
    padding: 0;
    margin-left: auto;

    @media (min-width: 1000px) {
        display: none;
    }

    .lines {
        position: relative;
        width: 26px;
        height: 16px;
        display: block;
    }

    .lines span {
        position: absolute;
        left: 0;
        width: 100%;
        height: 2px;
        background: #ffffff;
        transition: transform 350ms cubic-bezier(0.215, 0.61, 0.355, 1),
            opacity 250ms ease,
            top 350ms cubic-bezier(0.215, 0.61, 0.355, 1);
    }

    .lines span:nth-child(1) {
        top: ${({ $open }) => ($open ? '7px' : '0')};
        transform: ${({ $open }) => ($open ? 'rotate(45deg)' : 'none')};
    }
    .lines span:nth-child(2) {
        top: 7px;
        opacity: ${({ $open }) => ($open ? 0 : 1)};
    }
    .lines span:nth-child(3) {
        top: ${({ $open }) => ($open ? '7px' : '14px')};
        transform: ${({ $open }) => ($open ? 'rotate(-45deg)' : 'none')};
    }
`

export const MobileOverlay = styled.div<{ $open: boolean }>`
    position: fixed;
    inset: 0;
    z-index: 1100;
    background: #000000;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    /* Clear sticky header so logo/toggle stay put above the panel. */
    padding: calc(12px + 65px + 12px + 24px) 20px 48px;
    visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
    pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    transform: ${({ $open }) => ($open ? 'translateY(0)' : 'translateY(-100%)')};
    transition:
        opacity 700ms cubic-bezier(0.25, 0.46, 0.45, 0.94),
        transform 700ms cubic-bezier(0.25, 0.46, 0.45, 0.94),
        visibility 0s linear ${({ $open }) => ($open ? '0s' : '700ms')};

    @media (min-width: 1000px) {
        display: none;
    }
`

export const MobileNav = styled.ul`
    list-style: none;
    margin: 0 auto;
    padding: 0;
    width: min(520px, 100%);
    text-align: center;
`

export const MobileNavItem = styled.li<{ $open?: boolean; $index?: number }>`
    margin: 0 0 8px;
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    transform: ${({ $open }) => ($open ? 'translateY(0)' : 'translateY(110px)')};
    transition: ${({ $open }) =>
        $open
            ? `opacity 800ms cubic-bezier(0.19, 1, 0.22, 1),
               transform 800ms cubic-bezier(0.19, 1, 0.22, 1)`
            : `opacity 700ms cubic-bezier(0.25, 0.46, 0.45, 0.94),
               transform 0ms linear 700ms`};
    transition-delay: ${({ $open, $index = 0 }) =>
        $open ? `${370 + $index * 50}ms` : '0ms'};

    > a,
    > button {
        display: inline-block;
        background: transparent;
        border: 0;
        color: #ffffff;
        font-family: var(--font-display);
        font-size: 34px;
        line-height: 46px;
        letter-spacing: 1px;
        text-decoration: none;
        cursor: pointer;
        padding: 4px 0;
    }

    > a:hover,
    > button:hover {
        color: var(--brand);
    }
`

export const MobileSubNav = styled.ul`
    list-style: none;
    margin: 8px 0 20px;
    padding: 0;

    a,
    button {
        display: inline-block;
        background: transparent;
        border: 0;
        color: rgba(255, 255, 255, 0.78);
        font-family: var(--font-heading);
        font-size: 18px;
        line-height: 28px;
        text-decoration: none;
        cursor: pointer;
        padding: 4px 0;
    }

    a:hover,
    button:hover {
        color: var(--brand);
    }
`

export const MobileNested = styled.div`
    margin: 4px 0 16px;

    .group-title {
        display: block;
        color: #ffffff;
        font-family: var(--font-display);
        font-size: 22px;
        line-height: 30px;
        letter-spacing: 1px;
        margin: 12px 0 6px;
    }

    .company-link {
        display: block;
        color: #ffffff;
        text-decoration: none;
        font-family: var(--font-display);
        font-size: 22px;
        line-height: 30px;
        letter-spacing: 1px;
        margin: 12px 0 4px;
    }

    .company-desc {
        display: block;
        color: rgba(255, 255, 255, 0.55);
        font-family: var(--font-body);
        font-size: 14px;
        margin-top: 4px;
    }
`
